import { Link } from "react-router-dom";
import { ExternalLink, ShieldCheck } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";

/**
 * Public privacy notice. The canonical version is the Medicys Research
 * Participant Privacy Notice , this page mirrors it in full so visitors
 * don't have to leave the site, and links back to the canonical source.
 *
 * If Medicys updates the canonical notice, update the inlined content here
 * to match (or, eventually, fetch it server-side and cache it).
 */
export default function PrivacyPage() {
  usePageMeta({
    path: "/privacy",
    title: "Privacy notice , How MediTalk handles your data · MediTalk",
    description:
      "How Medicys Limited (trading as MediTalk) collects, uses, stores and shares personal data from research participants. Your rights under UK GDPR.",
  });

  return (
    <div className="bg-white">
      {/* Hero , DARK */}
      <section className="bg-[var(--dark-bg)] grain-dark relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full bg-[var(--lime)]/10 blur-3xl pointer-events-none"
        />
        <div className="container-main py-12 md:py-16 relative z-10 max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-3">
            <ShieldCheck size={14} />
            Legal
          </span>
          <h1 className="font-['Outfit'] font-bold text-white tracking-tight text-3xl md:text-5xl mb-4 leading-[1.05]">
            Research participant privacy notice
          </h1>
          <p className="text-base md:text-lg text-white/80 leading-relaxed max-w-2xl">
            How Medicys<span className="text-[0.55em] align-top tracking-normal ml-px" aria-label="registered trademark">®</span> Limited (trading as MediTalk) collects, uses, stores
            and shares your personal data when you take part in our research.
          </p>
          <p className="text-[12.5px] text-white/55 mt-5">
            Established 2018 · Last updated 17 December 2025
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="py-12 md:py-16">
        <div className="container-main max-w-3xl prose-body">
          <div className="text-[15px] md:text-[15.5px] text-[var(--text-muted)] leading-relaxed space-y-7">
            <p>
              Medicys Limited is committed to protecting the information we
              collect from each participant. The following notice outlines how
              we use that information.
            </p>
            <p>
              Medicys Limited is a member of, and complies with, the Codes of
              Conduct and Legal and Ethical Standards as prescribed by the
              BHBIA, EphMRA and ESOMAR. These guidelines are extremely
              comprehensive, and act as a pillar of our business processes
              (including how we protect the rights of respondents, handle data,
              and record adverse events and product complaints).
            </p>

            <Section title="1. Data controller and contacts">
              <p>
                <strong>Data controller:</strong> Medicys Limited (company
                number 04504403). Registered address: 2nd Floor Medway Bridge
                House, 1–8 Fairmeadow, Maidstone, Kent, United Kingdom, ME14
                1JP.
              </p>
              <p>
                <strong>Data Protection Officer:</strong> Richard Cowland,
                email{" "}
                <a className="ml-policy-link" href="mailto:privacy@medicysltd.co.uk">
                  privacy@medicysltd.co.uk
                </a>
                .
              </p>
              <p>
                <strong>EU Representative:</strong> Marie Payraudeau,{" "}
                <a className="ml-policy-link" href="mailto:m.payraudeau@medicysltd.co.uk">
                  m.payraudeau@medicysltd.co.uk
                </a>
                , +33 6 45 50 29 98.
              </p>
              <p>
                For questions, to exercise your rights, or to make a complaint,
                please contact{" "}
                <a className="ml-policy-link" href="mailto:privacy@medicysltd.co.uk">
                  privacy@medicysltd.co.uk
                </a>{" "}
                or write to the Data Protection Officer at the address above.
              </p>
            </Section>

            <Section title="2. Scope and purpose">
              <p>
                This notice explains how Medicys collects, uses, stores and
                shares personal data when you participate in research studies
                we conduct or manage. This includes, but is not limited to,
                observational research, qualitative interviews, online
                surveys, patient experience and health economics work, and
                related administrative activities. The notice applies whether
                you are a patient, caregiver, healthcare professional or other
                research participant.
              </p>
            </Section>

            <Section title="3. Categories of personal data we collect">
              <p>
                We collect only the personal data necessary for each study.
                Categories may include:
              </p>
              <ul className="ml-list">
                <li>Contact details and identifiers: name, email, telephone number, postal address.</li>
                <li>Professional information for healthcare professionals: job title, speciality, employer or affiliated institution.</li>
                <li>Health and clinical information for patients: diagnosis, treatment history and other health information relevant to the study. This is special category personal data.</li>
                <li>Research responses: survey answers, interview notes, and any text, audio or video recordings you provide.</li>
                <li>Administrative information: payment or honorarium details, audit logs, and records required for legal or contractual compliance.</li>
                <li>Technical data used for study integrity: IP address, browser and device information, timestamps and cookies where required to prevent fraud or duplicate entries. Raw IP addresses are retained for 90 days and then automatically deleted; a one-way cryptographic hash is retained for the same period as the study record so we can detect repeated submissions from the same source without keeping the underlying personal data.</li>
              </ul>
              <p>
                We will not collect highly sensitive personal data outside the
                scope of the study unless we have a clear legal basis and your
                explicit agreement.
              </p>
            </Section>

            <Section title="4. Lawful bases for processing and special category treatment">
              <p>
                We process personal data only where there is a lawful basis
                under applicable data protection law. We process health data
                and other special category personal data only where we have
                explicit consent or another Article 9 legal basis as
                appropriate and disclosed in the study documentation.
              </p>
              <p>Typical lawful bases we rely on are as follows:</p>
              <ul className="ml-list">
                <li>Recruitment, contacting and scheduling participants: explicit consent or legitimate interest (Article 6(1)(a), Article 6(1)(f)).</li>
                <li>Collection and use of health data for research objectives: explicit consent for processing special category personal data (Article 9(2)(a)).</li>
                <li>Reporting adverse events or other matters required by law: legal obligation (Article 6(1)(c)).</li>
                <li>Quality control, de-duplication and fraud prevention: legitimate interests (Article 6(1)(f)).</li>
              </ul>
              <p>We will perform and document a balancing test on request.</p>
              <p>
                Where processing is undertaken on instruction of a sponsor and
                the sponsor is the controller, Medicys will operate as a
                processor under a written contract.
              </p>
              <p>
                If we intend to rely on a different legal basis for processing
                activities, this will be made clear in the study-specific
                information you receive.
              </p>
            </Section>

            <Section title="5. What we do with your data and who we share it with">
              <p>
                Your data will be used to conduct, analyse and report the
                research. This includes scheduling, moderating and processing
                responses, transcription and translation where required,
                analysing results and issuing any agreed payments.
              </p>
              <p>We may share personal data with the following categories of recipients:</p>
              <ul className="ml-list">
                <li>The research sponsor or client may receive study data. If any identifiable information is shared, this will be clearly stated in the study documents and will only happen with your consent, unless sharing is required or permitted by law.</li>
                <li>Third-party service providers who act as data processors and who perform functions such as survey hosting, transcription, translation, secure storage, payment administration and analytics. All processors act on our instructions and are subject to data processing agreements containing appropriate technical and organisational safeguards.</li>
                <li>Regulatory bodies and public authorities where required by law, for example for adverse event reporting.</li>
                <li>Where required by local transparency or disclosure laws, such as reporting payments to healthcare professionals, we will disclose the information necessary to comply with those laws and we will advise you of the disclosure where permitted.</li>
              </ul>
              <p>
                We will state whether a recipient acts as controller or
                processor in the study-specific consent form. If we share
                identifiable data with another controller, you will be told who
                that controller is and the legal basis for the sharing.
              </p>
            </Section>

            <Section title="6. International transfers">
              <p>
                Your information may be transferred to recipients or processors
                outside the United Kingdom, the European Economic Area or your
                home jurisdiction. Where data is transferred to countries
                without an adequacy decision, we will rely on appropriate
                safeguards such as the UK or EU Standard Contractual Clauses,
                binding corporate rules, or other lawful transfer mechanisms.
                Copies of the safeguards are available on request. For
                transfers that require it, we will conduct a transfer risk
                assessment and implement additional protections where
                necessary.
              </p>
            </Section>

            <Section title="7. Retention">
              <p>
                We retain personal data only for as long as is necessary for
                the purposes described and to meet legal or contractual
                requirements. Indicative retention periods are:
              </p>
              <ul className="ml-list">
                <li>Recruitment contact details: for the duration of the study and typically up to 12 months afterwards for audit and queries, unless you consent to longer retention for re-contact.</li>
                <li>Audio and video recordings: normally up to 12 months after study completion, after which recordings will be deleted or irreversibly anonymised unless a longer period is necessary and disclosed in the study materials.</li>
                <li>Adverse event records: a minimum of 5 years, or longer where required by law or by the sponsor contract.</li>
                <li>Anonymised research data: may be retained indefinitely for research, audit and publication.</li>
                <li>Technical logs used for fraud prevention: retained for a short period such as 30 days unless required for an investigation.</li>
              </ul>
              <p>
                Specific retention obligations that differ from the above will
                be set out in the study-specific consent form. If you withdraw
                consent, we will stop further processing and delete personal
                identifiers within the periods set out above, except where we
                are required to retain records by law.
              </p>
            </Section>

            <Section title="8. Your rights">
              <p>
                Subject to local law, you have the following rights in relation
                to your personal data:
              </p>
              <ul className="ml-list">
                <li>The right to access your personal data and to receive a copy.</li>
                <li>The right to have inaccurate or incomplete personal data corrected.</li>
                <li>The right to request deletion of your personal data, in certain circumstances.</li>
                <li>The right to request restriction of processing in certain circumstances.</li>
                <li>The right to object to processing based on legitimate interests.</li>
                <li>The right to withdraw consent at any time where processing is based on consent, without affecting processing that took place before withdrawal.</li>
                <li>The right to data portability in a structured, commonly used, machine-readable format.</li>
                <li>The right to lodge a complaint with a supervisory authority, for example the Information Commissioner's Office in the United Kingdom.</li>
              </ul>
              <p>
                To exercise any right, please contact{" "}
                <a className="ml-policy-link" href="mailto:privacy@medicysltd.co.uk">
                  privacy@medicysltd.co.uk
                </a>{" "}
                or write to the Data Protection Officer at the registered
                office address above. We will verify your identity in
                accordance with our procedures and respond within the time
                limits required by law, normally within one month. Where
                complexity or a high number of requests requires additional
                time, we will advise you and extend the period by up to two
                months.
              </p>
            </Section>

            <Section title="9. Automated decision-making and profiling">
              <p>
                We may use automated tools to assist with pre-screening and
                fraud detection. Where solely automated decision-making is
                used that produces legal effects or similarly significant
                effects for you, we will notify you in the study materials,
                explain the logic involved and inform you of your right to
                request human review. For routine automated processing
                supporting scheduling or suitability checks, these will not
                usually produce legal or similarly significant effects.
              </p>
            </Section>

            <Section title="10. Security">
              <p>
                We take appropriate technical and organisational measures to
                protect personal data. Measures include encryption in transit
                and at rest, role-based access controls, multi-factor
                authentication for privileged accounts, logging and monitoring
                of access, secure development and deployment practices, and
                contractual obligations on processors to meet appropriate
                security standards.
              </p>
              <p>
                We require third-party processors to implement suitable
                safeguards. Where practical we prefer vendors who hold
                recognised information security certificates. We undertake
                periodic security reviews and testing. In the event of a
                personal data breach, we will assess the risk and, where
                required by law, notify the relevant supervisory authority and
                any affected individuals without undue delay.
              </p>
            </Section>

            <Section title="11. Adverse event reporting">
              <p>
                If you disclose an adverse event during a study, we will
                collect the minimum personal and clinical data necessary to
                fulfil reporting obligations to the sponsor or regulatory
                authorities. Where follow-up is required, and if permissible,
                we will ask for your permission to collect or provide
                identifying information.
              </p>
              <p>
                Where the law requires us to disclose identifiable information
                without consent, we will notify you whenever permitted and
                explain the legal basis for the disclosure.
              </p>
            </Section>

            <Section title="12. Payments and transparency for healthcare professionals">
              <p>
                Payments or honoraria will be administered as agreed for the
                study. Where local transparency laws require disclosure of
                payments made to healthcare professionals, we will comply with
                those legal obligations and inform the participant where
                disclosure is required and permissible.
              </p>
            </Section>

            <Section title="13. Children and vulnerable participants">
              <p>
                If a study involves children or other vulnerable participants,
                we will obtain parental or guardian consent and age-appropriate
                assent as required by law and ethical guidance. Age
                verification procedures will be used where necessary.
              </p>
            </Section>

            <Section title="14. Cookies, tracking and fraud prevention">
              <p>
                To protect study integrity, we may collect technical
                information such as IP addresses, browser details, device
                identifiers and timestamps. We may use cookies or similar
                technologies to prevent duplicate entries and detect fraud.
                Such technical data is kept only as long as necessary for these
                purposes and is deleted in accordance with the retention
                schedule. For details on the cookies meditalk.world sets, see
                our{" "}
                <Link to="/cookies" className="ml-policy-link">
                  cookie policy
                </Link>
                .
              </p>
            </Section>

            <Section title="15. Transcription, translation and subcontractors">
              <p>
                Audio and video recordings may be transcribed or translated by
                third-party providers. We require such providers to operate
                under a confidentiality obligation and a data processing
                agreement. Where translation or transcription requires
                transfer outside the UK or EEA, we will ensure appropriate
                transfer safeguards.
              </p>
            </Section>

            <Section title="16. Changes to this notice">
              <p>
                We may update this notice to reflect changes in legal or
                regulatory requirements or in our processing practices. We
                will post the revised notice on our website and the
                study-information page. Where changes are material, we will
                inform participants as required by law.
              </p>
            </Section>

            <Section title="17. Supervisory authority">
              <p>
                You have the right to lodge a complaint with your local
                supervisory authority, for example the Information
                Commissioner's Office in the United Kingdom, at{" "}
                <a
                  className="ml-policy-link"
                  href="https://ico.org.uk"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  ico.org.uk
                </a>
                . Our ICO registration number is{" "}
                <strong>Z1909295</strong>.
              </p>
            </Section>

            <Section title="18. Further information">
              <p>
                If you have questions about this notice or how we process
                personal data, or if you wish to exercise your rights, contact{" "}
                <a className="ml-policy-link" href="mailto:privacy@medicysltd.co.uk">
                  privacy@medicysltd.co.uk
                </a>{" "}
                or write to the Data Protection Officer at the registered
                office address above.
              </p>
            </Section>

            <div className="border-t border-[var(--light-border)] pt-6 text-[13px]">
              <p>
                The canonical version of this notice is hosted at{" "}
                <a
                  href="https://medicysltd.co.uk/research-participant-privacy-notice/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-policy-link inline-flex items-center gap-1"
                >
                  medicysltd.co.uk/research-participant-privacy-notice
                  <ExternalLink size={11} />
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section>
    <h2 className="font-['Outfit'] font-bold text-[var(--charcoal)] text-xl md:text-2xl mb-3">
      {title}
    </h2>
    <div className="space-y-3">{children}</div>
  </section>
);
